import { ClickAwayListener, Tooltip, Typography } from "@mui/material";
import { useContext, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useFetchPasswordQuery } from "app/services/teachers";
import { ITeacher } from "features/Teachers";
import PasswordIcon from "components/SVGs/Password";
import CopyIcon from "components/SVGs/Copy";
import { FireBaseContext } from "contexts/FireBase";

type Props = {
  item: ITeacher;
};

const Password = ({ item }: Props) => {
  const [userId, setUserId] = useState<number>();
  const latestUserIdRef = useRef<number>();
  const { logEvent } = useContext(FireBaseContext);
  const { t } = useTranslation();
  const [showToolTip, setShowToolTip] = useState(false);
  const [copied, setCopied] = useState(false);
  const { data: passwordData, isFetching } = useFetchPasswordQuery(userId, {
    skip: !userId,
  });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);


  const displayToolTip = (teacher: ITeacher) => {
    logEvent("ClickOnGetTeacherPin");
    const sameUser = teacher.teacherId === userId;
    const validPassword = passwordData?._statusCode === 200;

    latestUserIdRef.current = teacher.teacherId;

    if (sameUser && validPassword) {
      setShowToolTip(true);
      copyPassword(passwordData._entity as string);
      return;
    }

    setUserId(teacher.teacherId);
    setCopied(false);
    setShowToolTip(true);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [userId]);

  useEffect(() => {
    if (
      userId &&
      passwordData?._statusCode === 200 &&
      userId === latestUserIdRef.current
    ) {
      copyPassword(passwordData._entity as string);
    }
  }, [passwordData, userId]);

  const copyPassword = async (password: string) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 500);
    } catch (error) {
      console.error("Clipboard copy failed", error);
    }
  };

  const handleTooltipClose = () => {
    setShowToolTip(false);
    setCopied(false);
  };

  const toolTipBody = () => {
    if (
      isFetching ||
      !passwordData ||
      userId !== latestUserIdRef.current ||
      passwordData?._statusCode !== 200
    ) {
      return (
        <Typography component="span" sx={{ fontSize: "12px" }}>
          {t("Loading...")}
        </Typography>
      );
    }
    return (
      <Typography
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CopyIcon
          sx={{
            width: "18px",
            height: "18px",
          }}
          onClick={() => copyPassword(passwordData._entity as string)}
        />
        <Typography component="span" sx={{ fontSize: "12px", marginLeft: 1 }}>
          {copied
            ? t("Copied!")
            : `${t("Copy Teacher Password")}: ${passwordData?._entity}`}
        </Typography>
      </Typography>
    );
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        title={toolTipBody()}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        slotProps={{ popper: { disablePortal: true } }}
        onClose={handleTooltipClose}
        open={showToolTip}
        arrow
        placement="top"
      >
        <div>
          <PasswordIcon
            onClick={() => displayToolTip(item)}
            sx={{
              cursor: "pointer",
              "& path": {
                fill: "rgb(105, 123, 140)",
              },
              "&:hover path": {
                fill: "rgb(97, 194, 238)",
              },
              "&:active path": {
                fill: "rgb(105, 123, 140)",
              },
            }}
          />
        </div>
      </Tooltip>
    </ClickAwayListener>
  );
};

export default Password;
